/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SpeedDialsFormView.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form view for creating and editing Speed Dial entries.
    Manages speed dial codes and destinations for quick dialing.
────────────────────────────────────────────────────────────────*/

import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Phone, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useSpeedDialData } from './hooks/useSpeedDialData';
import { useSpeedDialForm } from './hooks/useSpeedDialForm';
import { useSpeedDialSubmit } from './hooks/useSpeedDialSubmit';
import SpeedDialFormFields from './components/SpeedDialFormFields';
import { navigateToSpeedDialDuplicate } from './speedDialsActions';
import { useQuickList } from './hooks/useQuickList';
import type { SpeedDial } from './api/speedDialsApi';

export default function SpeedDialsFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const duplicateId = searchParams.get('duplicate');
  const mode = id === 'new' || duplicateId ? 'create' : 'edit';

  const { t } = useI18n();
  const { showError } = useNotifications();
  useFixedActionBar(true);

  const [classOfServices, setClassOfServices] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingClassOfServices, setLoadingClassOfServices] = useState(false);
  const classOfServicesLoadedRef = useRef(false);
  const showErrorRef = useRef(showError);
  const translateRef = useRef(t);

  useEffect(() => {
    showErrorRef.current = showError;
    translateRef.current = t;
  }, [showError, t]);

  useEffect(() => {
    if (classOfServicesLoadedRef.current) return;

    const loadClassOfServices = async () => {
      classOfServicesLoadedRef.current = true;
      setLoadingClassOfServices(true);
      try {
        const response = await fetch(buildApiUrl('/api/telephony/class-of-services'), {
          headers: getApiHeaders(),
        });
        if (!response.ok) {
          throw new Error('Failed to load Class of Services');
        }
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setClassOfServices(result.data.map((cos: { id: number; name: string }) => ({ id: cos.id, name: cos.name })));
        } else {
          throw new Error(result.message || 'Failed to load Class of Services');
        }
      } catch (error) {
        showErrorRef.current?.(error instanceof Error ? error.message : translateRef.current('speedDials.messages.failedToLoadCos'));
        setClassOfServices([]);
        classOfServicesLoadedRef.current = false; // Allow retry on error
      } finally {
        setLoadingClassOfServices(false);
      }
    };

    loadClassOfServices();
  }, []); // Empty dependency array - only run once on mount

  // Data hooks
  const {
    formData,
    setFormData,
    baseline,
    setBaseline,
    isLoading
  } = useSpeedDialData(id, duplicateId);

  // Form hooks
  const {
    errors,
    hasChanges,
    handleInputChange,
    validateForm
  } = useSpeedDialForm(formData, baseline, mode, setFormData);

  // Submit hooks
  const {
    isSaving,
    isDeleting,
    showDeleteModal,
    setShowDeleteModal,
    handleFormSubmit: handleFormSubmitInternal,
    handleCancel,
    handleDelete
  } = useSpeedDialSubmit(
    formData,
    baseline,
    hasChanges,
    validateForm,
    mode,
    id,
    setFormData,
    setBaseline
  );

  // Enhanced save handler
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleFormSubmitInternal();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && mode === 'edit' && id) {
      return { id, ...formData };
    }

    return savedData;
  }, [handleFormSubmitInternal, formData, mode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/speed-dialing',
    newPath: '/pbx/applications/speed-dialing/new',
    mode,
    getEditPath: (savedData) => `/pbx/applications/speed-dialing/${savedData.id || savedData.uuid}`,
    getFormData: () => formData,
    storageKey: 'speedDials:saveAndNewData',
  });

  // Quick List hook
  const {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect: handleQuickListItemSelect,
    currentItemId
  } = useQuickList(mode, id, duplicateId);

  const handleDuplicateFromForm = () => {
    if (!id || id === 'new') return;
    navigateToSpeedDialDuplicate(navigate, id);
  };

  // Navigate to new
  const handleAddNew = () => {
    navigate('/pbx/applications/speed-dialing/new');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Phone className="h-5 w-5 text-primary" />}
        title={mode === 'edit' ? `${t('speedDials.editTitle')} - ${formData.speedCode || ''}` : t('speedDials.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate('/pbx/applications/speed-dialing')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('speedDials.actions.quickList'),
            onClick: () => setShowQuickList(true)
          },
          // 2. Duplicate (edit mode only)
          ...(mode === 'edit'
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('speedDials.actions.duplicate'),
                onClick: handleDuplicateFromForm
              }
            ]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('speedDials.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8">
        <form onSubmit={(e) => { e.preventDefault(); }} className="space-y-6">
          <SpeedDialFormFields
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            classOfServices={classOfServices}
            loadingClassOfServices={loadingClassOfServices}
          />

          <FixedActionBar
            onSaveWithAction={handleSaveWithAction}
            onCancel={handleCancel}
            onDelete={mode === 'edit' ? () => setShowDeleteModal(true) : undefined}
            isEditMode={mode === 'edit'}
            cancelText={t('common.cancel')}
            deleteText={t('common.delete')}
            isSaving={isSaving}
            deleteDisabled={isDeleting}
            useSaveDropdown={true}
            saveDropdownStorageKey="speedDials:saveAction"
            saveDisabled={isSaving}
          />
        </form>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('speedDials.messages.deleteTitle')}
        message={t('speedDials.messages.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('speedDials.title')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={handleQuickListItemSelect}
        currentItemId={currentItemId}
        renderItem={(item: SpeedDial, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono font-semibold text-foreground">{item.speedCode || t('common.unknown')}</p>
              {item.destination && (
                <p className="text-sm text-muted-foreground font-mono">{item.destination}</p>
              )}
              {item.description && (
                <p className="text-xs text-muted-foreground">{item.description}</p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('speedDials.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('speedDials.quickListSearchPlaceholder', 'Search by code or destination')}
        emptyMessage={t('speedDials.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('speedDials.quickListEmpty', 'No speed dials found')}
        headerIcon={<Phone className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
